import { ChangeDetectionStrategy, Component, effect, input, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { SharedModule } from '@shared/shared.module';
import { HdPropertyFilterOption } from './hd-chart-property-filter-options';

/**
 * Multi-select voor de eigenschappen-filterbalk met **apply-on-close**: tijdens
 * het aanvinken wijzigt er niets (geen request-storm, dropdown blijft open),
 * pas bij het sluiten wordt de gewijzigde selectie geëmit. Selecties tonen als
 * ng-select's native chips (pill + eigen × per item); een chip-× bij gesloten
 * paneel commit direct — er volgt dan immers geen close-event meer.
 *
 * Toetsenbord: getypte tekst + Enter kiest de waarde en maakt het zoekveld weer
 * leeg voor de volgende; Enter zonder getypte tekst past de filter toe (zie
 * {@link handleKeyDown}), net als Tab of ernaast klikken. Een optionele hint
 * onderaan de open lijst legt dat uit.
 */
@Component({
  selector: 'hd-chart-property-filter',
  standalone: true,
  imports: [FormsModule, NgSelectModule, SharedModule],
  template: `
    <ng-select
      #select
      class="hd-property-filter"
      [style.min-width]="minWidth()"
      [style.max-width]="minWidth()"
      [items]="options()"
      bindValue="value"
      bindLabel="label"
      [multiple]="true"
      [closeOnSelect]="false"
      [clearSearchOnAdd]="true"
      [clearable]="true"
      [placeholder]="label()"
      [searchFn]="searchFn()"
      [keyDownFn]="keyDown"
      [ngModel]="pending()"
      (ngModelChange)="pending.set($event ?? [])"
      (open)="navigatedSinceSelection = false"
      (add)="navigatedSinceSelection = false"
      (close)="commit()"
      (remove)="onItemRemoved(select.isOpen())"
      (clear)="commit([])"
    >
      <!-- Chip: optioneel een korter label (poortfilter: enkel het nummer). -->
      <ng-template ng-label-tmp let-item="item" let-clear="clear">
        <span class="ng-value-icon left" (click)="clear(item)" aria-hidden="true">×</span>
        <span class="ng-value-label">{{ item.chipLabel ?? item.label }}</span>
      </ng-template>
      <ng-template ng-option-tmp let-item="item">
        <span class="hd-property-filter-option">
          @if (item.icon) {
            <i eIcon [icon]="item.icon"></i>
          }
          {{ item.label }}
        </span>
      </ng-template>
      @if (applyHint(); as hint) {
        <!-- Enkel zichtbaar zolang de lijst open is: neemt geen plaats in de filterbalk. -->
        <ng-template ng-footer-tmp>
          <div class="hd-property-filter-hint">
            <i class="fas fa-keyboard" aria-hidden="true"></i>
            <span>{{ hint }}</span>
          </div>
        </ng-template>
      }
    </ng-select>
  `,
  styleUrl: './hd-chart-property-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartPropertyFilterComponent {
  readonly label = input.required<string>();
  readonly options = input.required<HdPropertyFilterOption[]>();
  readonly selected = input<string[]>([]);
  /** Vaste breedte van het veld; smallere velden voor korte waardesets (definitie, profiel). */
  readonly minWidth = input('160px');
  /** Eigen zoekfunctie (bv. exacte poortnummers); null = ng-select's standaard op het label. */
  readonly searchFn = input<((term: string, option: HdPropertyFilterOption) => boolean) | null>(null);
  /** Korte uitleg onderaan de open lijst (hoe je de filter toepast); null = geen. */
  readonly applyHint = input<string | null>(null);
  readonly selectedChange = output<string[]>();

  protected readonly pending = signal<string[]>([]);

  private readonly select = viewChild.required<NgSelectComponent>('select');

  protected readonly keyDown = (event: KeyboardEvent): boolean => this.handleKeyDown(event);

  /** Pijltjes gebruikt sinds de laatste keuze: Enter kiest dan de gemarkeerde waarde. */
  protected navigatedSinceSelection = false;

  constructor() {
    // Extern gezette selectie (bv. reset) overschrijft de pending-state.
    effect(() => this.pending.set([...this.selected()]));
  }

  /**
   * Enter kiest enkel een waarde wanneer de gebruiker er een aanwijst: getypte tekst
   * (de eerste treffer is gemarkeerd) of pijltjes. Anders past Enter de filter toe en
   * sluit de lijst, net als Enter op een al gekozen waarde, i.p.v. ng-select's
   * standaard: die waarde weer uitvinken. Een tweede Enter na het kiezen voelt als
   * "toepassen" maar haalde de keuze weg (Steven 2026-10-21).
   *
   * BELANGRIJK: het zoekveld wordt na een keuze leeggemaakt (`clearSearchOnAdd`),
   * maar ng-select laat de markering dan op dezelfde positie in de volledige lijst
   * staan (een andere waarde). Zonder deze regel zou die tweede Enter dus een
   * willekeurige waarde aanvinken (bv. poort 1). Specs: "Enter" in de spec van deze
   * component. Uitvinken kan nog met een klik op de waarde of het chip-×.
   * Geeft false terug = ng-select's eigen Enter-afhandeling overslaan.
   */
  private handleKeyDown(event: KeyboardEvent): boolean {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      this.navigatedSinceSelection = true;
      return true;
    }
    if (event.key !== 'Enter') return true;
    const select = this.select();
    if (!select.isOpen()) return true;
    const marked = select.itemsList.markedItem;
    const pointing = !!select.searchTerm || this.navigatedSinceSelection;
    if (pointing && marked && !marked.selected && !marked.disabled) return true;
    event.preventDefault();
    select.close();
    return false;
  }

  /** Chip-× geklikt: bij een gesloten paneel meteen toepassen (ngModel is dan al bijgewerkt). */
  protected onItemRemoved(panelIsOpen: boolean): void {
    if (!panelIsOpen) this.commit();
  }

  /** Emit alleen bij een echte wijziging t.o.v. de laatst toegepaste selectie. */
  protected commit(override?: string[]): void {
    const next = override ?? this.pending();
    if (override) this.pending.set(override);
    const current = this.selected();
    const changed = next.length !== current.length || next.some(value => !current.includes(value));
    if (changed) this.selectedChange.emit([...next]);
  }
}
